import React, { useState, useEffect } from 'react';
import Icon from './Icon';

const ADMIN_LOGS_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/v1/admin/logs'
  : '/v1/admin/logs';

const LEVEL_COLORS = {
  INFO:    { bg: '#EFF6FF', text: '#1D4ED8' },
  ERROR:   { bg: '#FEF2F2', text: '#DC2626' },
  WARN:    { bg: '#FFFBEB', text: '#D97706' },
  WARNING: { bg: '#FFFBEB', text: '#D97706' },
  DEBUG:   { bg: '#F3F4F6', text: '#6B7280' },
};

const TAG_PALETTE = [
  '#2563EB', '#7C3AED', '#0891B2', '#D97706', '#DB2777',
  '#059669', '#4F46E5', '#B45309', '#0D9488', '#BE185D', '#CA8A04',
];

function colorForTag(tag) {
  if (!tag) return '#6B7280';
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) >>> 0;
  return TAG_PALETTE[hash % TAG_PALETTE.length];
}

function formatTimestamp(ts) {
  const m = ts.match(/T(\d{2}:\d{2}:\d{2})\.(\d+)/);
  if (!m) return ts;
  return `${m[1]}.${m[2].slice(0, 3)}`;
}

// Parses one log line into { timestamp, level, tag, message, data }.
// Lines look like either:
//   2026-06-24T11:43:17.409973+00:00 [INFO] [pnc_priority_filter] Filtered rows | {"a": 1}
//   2026-06-24T11:42:50.975931+00:00 Retrieved 9 excluded PNC codes
//   2026-06-24T11:43:05.738878+00:00 [PreProcessor] Analyzing query...
function parseLogLine(line) {
  const spaceIdx = line.indexOf(' ');
  if (spaceIdx === -1) return { timestamp: line, level: '', tag: '', message: '', data: '' };

  const timestamp = line.slice(0, spaceIdx);
  let rest = line.slice(spaceIdx + 1);

  let level = '';
  let tag = '';

  const levelTagMatch = rest.match(/^\[(INFO|ERROR|DEBUG|WARNING|WARN)\]\s*\[([^\]]+)\]\s*([\s\S]*)$/);
  if (levelTagMatch) {
    level = levelTagMatch[1];
    tag = levelTagMatch[2];
    rest = levelTagMatch[3];
  } else {
    const tagMatch = rest.match(/^\[([^\]]+)\]\s*([\s\S]*)$/);
    if (tagMatch) {
      tag = tagMatch[1];
      rest = tagMatch[2];
    }
  }

  let message = rest;
  let data = '';
  const sepIdx = rest.indexOf(' | {');
  if (sepIdx !== -1) {
    message = rest.slice(0, sepIdx);
    data = rest.slice(sepIdx + 3);
  }

  return { timestamp, level, tag, message, data };
}

function LogLine({ line, even }) {
  const { timestamp, level, tag, message, data } = parseLogLine(line);
  const lvl = LEVEL_COLORS[level];
  const tagColor = colorForTag(tag);

  return (
    <div style={{
      display: 'flex', gap: 14, padding: '7px 10px', borderRadius: 8,
      background: even ? '#FAFBFC' : 'transparent', alignItems: 'flex-start',
    }}>
      <span style={{
        flexShrink: 0, minWidth: 110, color: '#A3ACBA', fontSize: 12.5,
        fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
        paddingTop: 1,
      }}>
        {formatTimestamp(timestamp)}
      </span>
      <span style={{ flex: 1, fontSize: 13.5, lineHeight: 1.75, color: '#1F2937', wordBreak: 'break-word' }}>
        {level && (
          <span style={{
            display: 'inline-block', background: lvl.bg, color: lvl.text, fontWeight: 700,
            fontSize: 10.5, padding: '2px 7px', borderRadius: 5, marginRight: 7,
            verticalAlign: 1,
          }}>
            {level}
          </span>
        )}
        {tag && (
          <span style={{ color: tagColor, fontWeight: 700, marginRight: 7 }}>
            [{tag}]
          </span>
        )}
        <span>{message}</span>
        {data && (
          <span style={{
            color: '#9CA3AF', marginLeft: 8, fontSize: 12.5,
            fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
          }}>
            {data}
          </span>
        )}
      </span>
    </div>
  );
}

export default function LogViewerModal({ isOpen, onClose, chatRecordId }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [log,     setLog]     = useState(null);

  useEffect(() => {
    if (!isOpen || !chatRecordId) return;

    setLoading(true);
    setError(null);
    setLog(null);

    const userId = sessionStorage.getItem('user_id') || localStorage.getItem('user_id');

    fetch(`${ADMIN_LOGS_URL}/${encodeURIComponent(chatRecordId)}?user_id=${encodeURIComponent(userId || '')}`)
      .then(r => r.json().then(data => ({ ok: r.ok, status: r.status, data })))
      .then(({ ok, status, data }) => {
        if (!ok) {
          throw new Error(status === 404 ? 'No log found for this chat.' : (data.error || 'Failed to load log.'));
        }
        setLog(data);
      })
      .catch(err => setError(err.message || 'Failed to load log.'))
      .finally(() => setLoading(false));
  }, [isOpen, chatRecordId]);

  if (!isOpen) return null;

  const lines = log && log.log_text ? log.log_text.split('\n').filter(Boolean) : [];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 16, width: '100%', maxWidth: 1100,
          height: '85vh', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)', overflow: 'hidden',
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #E8EAED', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="log" size={16} color="#CC1A00" />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Request Log</span>
            {chatRecordId && (
              <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>{chatRecordId}</span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 6, color: '#6B7280',
            }}
          >
            <Icon name="x" size={16} color="#6B7280" />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
          {loading && (
            <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 14, padding: 48 }}>
              Loading log…
            </div>
          )}

          {!loading && error && (
            <div style={{ textAlign: 'center', color: '#DC2626', fontSize: 14, padding: 48 }}>
              {error}
            </div>
          )}

          {!loading && !error && lines.length > 0 && (
            <div style={{
              background: '#FFFFFF', border: '1px solid #E8EAED', borderRadius: 10,
              padding: '8px 6px',
            }}>
              {lines.map((line, i) => <LogLine key={i} line={line} even={i % 2 === 1} />)}
            </div>
          )}

          {!loading && !error && log && !log.log_text && (
            <div style={{ textAlign: 'center', color: '#6B7280', fontSize: 14, padding: '24px 0' }}>
              Log text could not be retrieved.
              {log.log_url && (
                <div style={{ marginTop: 10 }}>
                  <a href={log.log_url} target="_blank" rel="noreferrer" style={{ color: '#CC1A00', fontWeight: 600 }}>
                    Open raw log
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
